import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  formatFlightDateTime,
  parseLegacyDateTime,
  formatCompactDateTime
} from '../utils/dateUtils';

interface AccessRequest {
  id: string;
  email: string;
  full_name: string;
  phone_number: string;
  oauth_provider: string;
  status: string;
  created_at: string;
  admin_notes?: string;
}

interface ChangeRequest {
  id: string;
  guest_id: string;
  request_type: string;
  current_value: any;
  requested_value: any;
  current_status?: string;
  requested_status?: string;
  current_size?: string;
  requested_size?: string;
  item_type?: string;
  itemType?: string;
  event_name?: string;
  reason?: string;
  status: string;
  admin_notes?: string;
  created_at: string;
  guest_name?: string;
}

interface TransportationRequest {
  id: string;
  guestEmail: string;
  guestName?: string;
  requestType: string;
  transportationType: string;
  airline: string;
  flightNumber: string;
  flightDate: string;
  flightTime: string;
  passengerCount: number;
  specialRequirements?: string;
  pickupLocation?: string;
  destination: string;
  status: string;
  adminNotes?: string;
  createdAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`request-tabpanel-${index}`}
      aria-labelledby={`request-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const RequestManagement: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([]);
  const [eventChangeRequests, setEventChangeRequests] = useState<ChangeRequest[]>([]);
  const [sizeChangeRequests, setSizeChangeRequests] = useState<ChangeRequest[]>([]);
  const [transportationRequests, setTransportationRequests] = useState<TransportationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'deny'>('approve');
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [clearingRequests, setClearingRequests] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setError(null); // Clear any previous errors
      
      // Use the ApiService which has the correct API Gateway URL
      const { ApiService } = await import('../services/api');

      const [accessResult, changeRequestsResult] = await Promise.all([
        ApiService.getAccessRequests(),
        ApiService.getChangeRequests() // This now includes all types including transportation
      ]);

      // Handle access requests
      if (accessResult.success && accessResult.data?.requests) {
        const mappedRequests = accessResult.data.requests.map((request: any) => ({
          id: request.id,
          email: request.email,
          full_name: request.fullName || request.full_name,
          phone_number: request.phone || request.phone_number || '',
          oauth_provider: request.oauth_provider || 'email',
          status: request.status,
          created_at: request.createdAt || request.created_at,
          admin_notes: request.adminNotes || request.admin_notes || '',
          relationshipToHost: request.relationshipToHost,
          message: request.message
        }));
        setAccessRequests(mappedRequests);
      } else {
        setAccessRequests([]);
      }

      // Handle unified change requests - separate by type
      if (changeRequestsResult.success && changeRequestsResult.data) {
        const allRequests = changeRequestsResult.data;
        
        // Separate event changes
        const eventChanges = allRequests.filter((req: any) => req.requestType === 'event_change');
        setEventChangeRequests(eventChanges);
        
        // Separate size changes
        const sizeChanges = allRequests.filter((req: any) => req.requestType === 'size_change');
        setSizeChangeRequests(sizeChanges);
        
        // Separate transportation requests
        const transportRequests = allRequests.filter((req: any) => req.requestType === 'transportation');
        setTransportationRequests(transportRequests);
      } else {
        setEventChangeRequests([]);
        setSizeChangeRequests([]);
        setTransportationRequests([]);
      }

    } catch (err) {
      console.error('Error loading requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewRequest = (request: any, type: 'access' | 'event' | 'size' | 'transportation', action: 'approve' | 'deny') => {
    setSelectedRequest({ ...request, type });
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleDeleteTransportationRequest = async (requestId: string) => {
    if (!window.confirm('Are you sure you want to delete this cancelled transportation request? This action cannot be undone.')) {
      return;
    }

    try {
      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/api/transportation/admin/transportation/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete transportation request');
      }

      await loadRequests();
    } catch (err) {
      console.error('Error deleting transportation request:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete transportation request');
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedRequest) return;

    try {
      const { ApiService } = await import('../services/api');
      const apiBase = process.env.NODE_ENV === 'production' 
        ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod' 
        : 'http://localhost:3001';
      
      switch (selectedRequest.type) {
        case 'access':
          if (reviewAction === 'approve') {
            await ApiService.approveAccessRequest(selectedRequest.id);
          } else {
            await ApiService.rejectAccessRequest(selectedRequest.id);
          }
          break;
        case 'transportation':
          // Handle transportation requests through unified endpoint
          const transportEndpoint = `${apiBase}/api/admin/change-requests/${selectedRequest.id}/${reviewAction}`;
          const transportResponse = await fetch(transportEndpoint, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              adminNotes: reviewNotes
            }),
            mode: 'cors'
          });
          if (!transportResponse.ok) {
            const errorData = await transportResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to process transportation request');
          }
          break;
        case 'event':
          // Use direct API Gateway URL in production to avoid CloudFront routing issues
          const eventEndpoint = `${apiBase}/api/admin/event-changes/${selectedRequest.id}/${reviewAction}`;
          const eventResponse = await fetch(eventEndpoint, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              status: reviewAction,
              reviewNotes: reviewNotes
            }),
            mode: 'cors'
          });
          console.log('Event change response status:', eventResponse.status);
          if (!eventResponse.ok) {
            const errorData = await eventResponse.json().catch(() => ({}));
            console.error('Event change error:', errorData);
            throw new Error(errorData.error || 'Failed to process event change request');
          }
          // Check if response is JSON
          const eventContentType = eventResponse.headers.get('content-type');
          if (eventContentType && eventContentType.includes('application/json')) {
            const eventResult = await eventResponse.json();
            console.log('Event change success:', eventResult);
          } else {
            console.warn('Event change returned non-JSON response, but status was OK');
          }
          break;
        case 'size':
          // Use direct API Gateway URL in production to avoid CloudFront routing issues
          const sizeEndpoint = `${apiBase}/api/admin/size-change-requests/${selectedRequest.id}/${reviewAction}`;
          console.log('Calling size change endpoint:', sizeEndpoint);
          const sizeResponse = await fetch(sizeEndpoint, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              status: reviewAction,
              reviewNotes: reviewNotes
            }),
            mode: 'cors'
          });
          console.log('Size change response status:', sizeResponse.status);
          if (!sizeResponse.ok) {
            const errorData = await sizeResponse.json().catch(() => ({}));
            console.error('Size change error:', errorData);
            throw new Error(errorData.error || 'Failed to process size change request');
          }
          // Check if response is JSON
          const contentType = sizeResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const sizeResult = await sizeResponse.json();
            console.log('Size change success:', sizeResult);
          } else {
            console.warn('Size change returned non-JSON response, but status was OK');
          }
          break;
      }

      setReviewDialogOpen(false);
      setSelectedRequest(null);
      setReviewNotes('');
      loadRequests();
    } catch (err) {
      console.error('Error processing review:', err);
      setError(err instanceof Error ? err.message : 'Failed to process request');
    }
  };

  const handleClearAllRequests = async () => {
    setClearingRequests(true);
    try {
      const { ApiService } = await import('../services/api');
      
      // Use the ApiService clear all method
      await ApiService.clearAllRequests();

      // Refresh the data
      await loadRequests();
      
      setClearAllDialogOpen(false);
    } catch (err) {
      console.error('Error clearing requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear all requests');
    } finally {
      setClearingRequests(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'denied': return 'error';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) {
      return 'Unknown';
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid Date';
    }
    
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    // If less than 24 hours ago, show relative time
    if (diffInHours < 24 && diffInHours >= 0) {
      if (diffInHours < 1) {
        const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
      } else {
        const hours = Math.floor(diffInHours);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }
    }
    
    // Otherwise show full date and time
    try {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.warn('Error formatting date:', error);
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          Request Management
        </Typography>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setClearAllDialogOpen(true)}
          disabled={
            accessRequests.length === 0 && 
            eventChangeRequests.length === 0 && 
            sizeChangeRequests.length === 0 &&
            transportationRequests.length === 0
          }
        >
          Clear All Requests
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">Access Requests</Typography>
              <Typography variant="h4">
                {accessRequests.filter(r => r.status === 'pending').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                pending approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary">Event Changes</Typography>
              <Typography variant="h4">
                {eventChangeRequests.filter(r => r.status === 'pending').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                pending approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">Size Changes</Typography>
              <Typography variant="h4">
                {sizeChangeRequests.filter(r => r.status === 'pending').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                pending approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">Transportation</Typography>
              <Typography variant="h4">
                {transportationRequests.filter(r => r.status === 'pending').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                pending approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label={`Access Requests (${accessRequests.filter(r => r.status === 'pending').length})`} />
            <Tab label={`Event Changes (${eventChangeRequests.filter(r => r.status === 'pending').length})`} />
            <Tab label={`Size Changes (${sizeChangeRequests.filter(r => r.status === 'pending').length})`} />
            <Tab label={`Transportation (${transportationRequests.filter(r => r.status === 'pending').length})`} />
          </Tabs>
        </Box>

        {/* Access Requests Tab */}
        <TabPanel value={currentTab} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accessRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.full_name}</TableCell>
                    <TableCell>{request.email}</TableCell>
                    <TableCell>{request.phone_number}</TableCell>
                    <TableCell>
                      <Chip label={request.oauth_provider} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={request.status} 
                        size="small" 
                        color={getStatusColor(request.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateTime(request.created_at)}
                    </TableCell>
                    <TableCell>
                      {request.status === 'pending' && (
                        <Box display="flex" gap={1}>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReviewRequest(request, 'access', 'approve')}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deny">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReviewRequest(request, 'access', 'deny')}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Event Change Requests Tab */}
        <TabPanel value={currentTab} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Guest</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Current Status</TableCell>
                  <TableCell>Requested Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventChangeRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.guest_name || 'Unknown Guest'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.event_name || 'Unknown Event'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {request.current_status || request.current_value?.status || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {request.requested_status || request.requested_value?.status || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{request.reason || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={request.status} 
                        size="small" 
                        color={getStatusColor(request.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateTime(request.created_at)}
                    </TableCell>
                    <TableCell>
                      {request.status === 'pending' && (
                        <Box display="flex" gap={1}>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReviewRequest(request, 'event', 'approve')}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deny">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReviewRequest(request, 'event', 'deny')}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Size Change Requests Tab */}
        <TabPanel value={currentTab} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Guest</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Item Type</TableCell>
                  <TableCell>Current Size</TableCell>
                  <TableCell>Requested Size</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sizeChangeRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.guest_name || 'Unknown Guest'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.event_name || 'Unknown Event'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.item_type || request.itemType || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.current_size || request.current_value?.size || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.requested_size || request.requested_value?.size || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{request.reason || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={request.status} 
                        size="small" 
                        color={getStatusColor(request.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateTime(request.created_at)}
                    </TableCell>
                    <TableCell>
                      {request.status === 'pending' && (
                        <Box display="flex" gap={1}>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReviewRequest(request, 'size', 'approve')}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deny">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReviewRequest(request, 'size', 'deny')}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Transportation Requests Tab */}
        <TabPanel value={currentTab} index={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Guest</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Flight Info</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Passengers</TableCell>
                  <TableCell>Special Requirements</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transportationRequests
                  .sort((a, b) => {
                    // 1. Sort by status (pending first)
                    const statusOrder = { 'pending': 0, 'approved': 1, 'denied': 2, 'cancelled': 3 };
                    const statusDiff = (statusOrder[a.status as keyof typeof statusOrder] ?? 99) - 
                                      (statusOrder[b.status as keyof typeof statusOrder] ?? 99);
                    if (statusDiff !== 0) return statusDiff;
                    
                    // 2. Sort by flight date/time
                    const getDateTime = (req: TransportationRequest) => {
                      try {
                        // Handle new ISO format
                        if ((req as any).flightDateTime) {
                          return new Date((req as any).flightDateTime).getTime();
                        }
                        // Handle legacy format
                        if (req.flightDate && req.flightTime) {
                          return new Date(`${req.flightDate}T${req.flightTime}`).getTime();
                        }
                        return 0;
                      } catch {
                        return 0;
                      }
                    };
                    
                    const dateDiff = getDateTime(a) - getDateTime(b);
                    if (dateDiff !== 0) return dateDiff;
                    
                    // 3. Sort by airline
                    return (a.airline || '').localeCompare(b.airline || '');
                  })
                  .map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.guestEmail}</TableCell>
                    <TableCell>
                      <Chip 
                        label={request.transportationType === 'airport_to_hotel' ? 'Airport → Hotel' : 'Hotel → Airport'} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.airline} {request.flightNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        try {
                          // Handle new ISO format (flightDateTime)
                          if ((request as any).flightDateTime) {
                            const formatted = formatFlightDateTime((request as any).flightDateTime);
                            return (
                              <>
                                <Typography variant="body2">
                                  {formatted.date}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatted.time} {formatted.timezone}
                                </Typography>
                              </>
                            );
                          }
                          // Handle legacy format (separate flightDate and flightTime)
                          else if (request.flightDate && request.flightTime) {
                            const formatted = parseLegacyDateTime(request.flightDate, request.flightTime);
                            return (
                              <>
                                <Typography variant="body2">
                                  {formatted.date}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatted.time} {formatted.timezone}
                                </Typography>
                              </>
                            );
                          }
                          return <Typography variant="body2">Invalid date</Typography>;
                        } catch (error) {
                          console.error('Error formatting date:', error);
                          return <Typography variant="body2">Invalid date</Typography>;
                        }
                      })()}
                    </TableCell>
                    <TableCell>{request.passengerCount}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {request.specialRequirements || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={request.status} 
                        size="small" 
                        color={getStatusColor(request.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateTime(request.createdAt)}
                    </TableCell>
                    <TableCell>
                      {request.status === 'pending' && (
                        <Box display="flex" gap={1}>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReviewRequest(request, 'transportation', 'approve')}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deny">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReviewRequest(request, 'transportation', 'deny')}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {request.status === 'cancelled' && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteTransportationRequest(request.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === 'approve' ? 'Approve' : 'Deny'} Request
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to {reviewAction} this request?
            </Typography>
            
            {selectedRequest && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">Request Details:</Typography>
                <Typography variant="body2">
                  Type: {selectedRequest.type}
                </Typography>
                {selectedRequest.full_name && (
                  <Typography variant="body2">
                    Name: {selectedRequest.full_name}
                  </Typography>
                )}
                {selectedRequest.email && (
                  <Typography variant="body2">
                    Email: {selectedRequest.email}
                  </Typography>
                )}
              </Box>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label={reviewAction === 'approve' ? 'Approval Notes (Optional)' : 'Reason for Denial'}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder={
                reviewAction === 'approve' 
                  ? 'Add any notes for this approval...'
                  : 'Please provide a reason for denying this request...'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitReview} 
            variant="contained"
            color={reviewAction === 'approve' ? 'success' : 'error'}
          >
            {reviewAction === 'approve' ? 'Approve' : 'Deny'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Requests Dialog */}
      <Dialog open={clearAllDialogOpen} onClose={() => setClearAllDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clear All Requests</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to clear all requests? This action cannot be undone.
            </Typography>
            
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2">This will delete:</Typography>
              <Typography variant="body2">• {accessRequests.length} access requests</Typography>
              <Typography variant="body2">• {eventChangeRequests.length} event change requests</Typography>
              <Typography variant="body2">• {sizeChangeRequests.length} size change requests</Typography>
              <Typography variant="body2">• {transportationRequests.length} transportation requests</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)} disabled={clearingRequests}>
            Cancel
          </Button>
          <Button 
            onClick={handleClearAllRequests} 
            variant="contained"
            color="error"
            disabled={clearingRequests}
          >
            {clearingRequests ? <CircularProgress size={20} /> : 'Clear All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};